// Phase 44.31 — Admin: sezione "Sessioni attive" nel dettaglio utente.
//
// Mostra tutte le sessioni (attive + opzionalmente revocate) di un utente
// target. Permette all'admin di revocare una sessione specifica per supporto.

import { useEffect, useState } from "react";
import { Smartphone, Monitor, Tablet, Globe, Loader2, LogOut, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
  iphone: Smartphone, ipad: Tablet, android: Smartphone,
  mac: Monitor, windows: Monitor, linux: Monitor, unknown: Globe,
};

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-CH", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
  } catch { return iso; }
}

export default function AdminUserSessions({ userId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento sessioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const revokeOne = async (sid) => {
    setBusy(sid);
    try {
      await api.delete(`/admin/users/${userId}/sessions/${sid}`);
      toast.success("Sessione revocata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore revoca");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-xl border border-dapnow-line p-4 space-y-3" data-testid="admin-user-sessions">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-dapnow-blue" />
        <h3 className="font-display text-sm font-bold tracking-tight">Sessioni attive</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-dapnow-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-dapnow-muted">Nessuna sessione attiva per questo utente.</p>
      ) : (
        <ul className="space-y-2">
          {sessions.map((s) => {
            const Icon = ICONS[s.device_kind] || Globe;
            return (
              <li
                key={s.id}
                className="rounded-lg border border-dapnow-line bg-white px-3 py-2"
                data-testid={`admin-user-sessions-item-${s.id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-dapnow-muted" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{s.device_label}</p>
                    <p className="text-[11px] text-dapnow-muted truncate">
                      {s.ip || "IP sconosciuto"} · ultimo: {formatDt(s.last_seen_at)} · creata: {formatDt(s.created_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => revokeOne(s.id)}
                    disabled={busy === s.id}
                    data-testid={`admin-user-sessions-revoke-${s.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold border border-dapnow-red/30 text-dapnow-red hover:bg-dapnow-red/10 disabled:opacity-50"
                  >
                    {busy === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                    Revoca
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
